import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { configApi } from '../api/config';
import { onConsentChange, onOpenConsentSettings, readConsent, saveConsent } from '../lib/consent';
import { areaOf, configureTracking, onRouteChange } from '../lib/tracking';
import { useLandingPrefix } from './landing/LandingShell';

/**
 * The question, and the wiring that acts on the answer.
 *
 * It asks only when there is something to ask about: with no tag configured it renders nothing,
 * and it never appears inside an account, where nothing is tracked whatever the answer was.
 *
 * "Accept all" and "Only necessary" are the same size, the same weight and one click each. That is
 * not styling: consent obtained by making refusal harder than agreement is not consent, and a
 * regulator reads a banner by exactly that test.
 */
export default function ConsentBanner() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const prefix = useLandingPrefix();

  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });
  const tracking = config?.tracking ?? null;

  // The answer is state, not something read during render: answering from the short question
  // changes nothing else on this component, and a banner that stays up after "Only necessary"
  // because nothing told React to look again is a banner that reads as having ignored the click.
  const [consent, setConsent] = useState(readConsent);

  // Null until somebody opens it: undecided visitors see the short question, not the form.
  const [detail, setDetail] = useState<{ analytics: boolean; marketing: boolean } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => onConsentChange(setConsent), []);

  useEffect(() => {
    if (config) {
      configureTracking(tracking);
    }
  }, [config, tracking]);

  useEffect(() => {
    onRouteChange(pathname);
  }, [pathname]);

  useEffect(
    () =>
      onOpenConsentSettings(() => {
        const current = readConsent();
        setDetail({ analytics: current?.analytics ?? false, marketing: current?.marketing ?? false });
        setOpen(true);
      }),
    [],
  );

  const undecided = tracking !== null && consent === null && areaOf(pathname) === 'tracked';

  if (!tracking || (!open && !undecided)) {
    return null;
  }

  function decide(choice: { analytics: boolean; marketing: boolean }) {
    saveConsent(choice);
    setOpen(false);
    setDetail(null);
  }

  return (
    <section className="consent" role="dialog" aria-modal="false" aria-labelledby="consent-title">
      <h2 id="consent-title">{t('consent.title')}</h2>

      {detail === null ? (
        <>
          <p>
            {t('consent.body')} <Link to={`${prefix}/cookies`}>{t('consent.policyLink')}</Link>
          </p>
          <div className="consent__actions">
            <button type="button" className="consent__btn" onClick={() => decide({ analytics: true, marketing: true })}>
              {t('consent.acceptAll')}
            </button>
            <button type="button" className="consent__btn" onClick={() => decide({ analytics: false, marketing: false })}>
              {t('consent.onlyNecessary')}
            </button>
            <button
              type="button"
              className="consent__btn consent__btn--quiet"
              onClick={() => setDetail({ analytics: false, marketing: false })}
            >
              {t('consent.choose')}
            </button>
          </div>
        </>
      ) : (
        <>
          <ul className="consent__purposes">
            <li>
              <label>
                <input type="checkbox" checked disabled />
                <span>
                  <strong>{t('consent.necessary.title')}</strong>
                  {t('consent.necessary.body')}
                </span>
              </label>
            </li>
            {tracking.googleAnalyticsId && (
              <li>
                <label>
                  <input
                    type="checkbox"
                    checked={detail.analytics}
                    onChange={(e) => setDetail({ ...detail, analytics: e.target.checked })}
                  />
                  <span>
                    <strong>{t('consent.analytics.title')}</strong>
                    {t('consent.analytics.body')}
                  </span>
                </label>
              </li>
            )}
            {tracking.googleAdsId && (
              <li>
                <label>
                  <input
                    type="checkbox"
                    checked={detail.marketing}
                    onChange={(e) => setDetail({ ...detail, marketing: e.target.checked })}
                  />
                  <span>
                    <strong>{t('consent.marketing.title')}</strong>
                    {t('consent.marketing.body')}
                  </span>
                </label>
              </li>
            )}
          </ul>
          <p className="consent__note">
            {t('consent.insideAccount')} <Link to={`${prefix}/cookies`}>{t('consent.policyLink')}</Link>
          </p>
          <div className="consent__actions">
            <button type="button" className="consent__btn" onClick={() => decide(detail)}>
              {t('consent.save')}
            </button>
            <button type="button" className="consent__btn" onClick={() => decide({ analytics: true, marketing: true })}>
              {t('consent.acceptAll')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
